import React, { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { IState, IUserStateDefault, useDispatch, actions } from '~/redux';
import config from '~/config';
import content from '~/utils/contentAPI';
import { useSelector } from 'react-redux';
import { localStorageKeys } from '~/utils/localStorageKeys';
import { styled, colors } from '~/styles';
import backgroundImg from '~/assets/images/marble-background-large.jpg';
// import LogoImg from '~/assets/images/revance-studio-logo-black-v2.svg';
import LogoImg from '~/assets/images/revance-u-logo-v2.svg';
import { PageContainer } from './layout/PageBasics';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import ReactGA from 'react-ga';
import sso from '~/utils/ssoAPI';

const StyledPageContainer = styled(PageContainer)`
  display: flex;
  justify-content: center;
  align-items: center;

  .loading-container {
    .loading-logo {
      display: flex;
      margin-bottom: 57px;

      .loading-img {
        transform: translateY(-11%); // svg padding compensation
        width: 210px;
        height: 31px;
      }

      span {
        white-space: nowrap;
        margin-left: 19px;
        font-size: 26px;
        font-weight: 300;
        letter-spacing: 4px;
      }

      @media (max-width: 575px) {
        transform: scale(0.85);
      }

      @media (min-width: 1200px) {
        transform: scale(1.5);
      }
    }

    .loading-img {
      width: 75px;
      height: 91.86px;
      margin-bottom: 57px;
    }

    .loading-text {
      text-transform: uppercase;
      font-size: 20px;
      font-weight: bold;
    }
    .progress-bar {
      width: 250px;
      height: 5px;
      background-color: rgba(0, 0, 0, 0.2);
      .progress {
        width: 0;
        height: 100%;
        background-color: ${colors.black};
      }
    }
    .progress-text {
      margin-top: 1rem;
      font-size: 20px;
    }
  }
`

function PrivateRoute({ component: Component, ...rest }) {
  // redux state
  const entryParams = useSelector((state: IState) => state.entryParams);
  const dispatch = useDispatch();

  // local state
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);

  const progressSteps = 4;
  const incrementProgress = () => {
    setProgress(prevProgress => prevProgress + 1);
  }

  useEffect(() => {
    const init = async () => {
      let userState = IUserStateDefault;

      // BEGIN SSO
      try {
        if (!config.testUser) {
          // const ssoAuth: any = await sso.getAuth();
          const ssoAuth: any = await sso.decryptAuth(entryParams.ref);

          // if no error thrown, server indicates user is logged in
          if (config.debug) { console.log('/getauth SUCCESS', ssoAuth); }

          userState = {
            ...IUserStateDefault,
            uid: ssoAuth.status.username || '',
            username: ssoAuth.status.username || '',
            email: ssoAuth.status.email || '',
            confirmed: true,
            suspended: false,
            firstName: ssoAuth.status.name
          };
        } else {
          if (config.debug) { console.log('initializing test user (no login required)'); }

          userState = {
            ...IUserStateDefault,
            uid: 'testuser@viscira.com' || '',
            username: 'testuser@viscira.com' || '',
            email: 'testuser@viscira.com' || '',
            firstName: 'Test User' || '',
            confirmed: true,
            suspended: false
          };          
        }
        
        dispatch({
          type: actions.UPDATE_USER,
          payload: userState
        })

        localStorage.setItem(localStorageKeys.SID, userState.username);

        // Google Analytics: associate subsequent events with the user
        ReactGA.set({ userId: userState.username });

        incrementProgress();


        /**
         * Check if CRM user has a content API user account
         */
        const contentToken = await content.loadAPIToken();

        if (contentToken) {
          const users = await content.findUsers(userState.username) as any[];

          if (config.debug) { console.log('content /users loaded', users); }

          incrementProgress();

          const match = _.find(users, ['uid', userState.username]);

          if (config.debug) { console.log('found matching user', match, userState.username); }

          if (match) {
            // TODO
            if (config.debug) { console.log('update user info', userState, 'with the CRM data & get our content data for user', match); }

            userState = {
              ...userState,
              uid: userState.username,
              contentID: match.id,
              favoriteResources: match.favorited_resources
            }

            dispatch({
              type: actions.UPDATE_USER,
              payload: userState
            })

          } else {
            const createdUser = await content.createUser({
              'username': userState.username,
              'uid': userState.username,
              'email': userState.email === '' ? 'anonymous-' + uuidv4() + '@example.com' : userState.email,
              'password': uuidv4(),
              'confirmed': true,
              'firstName': userState.firstName,
              'lastName': userState.lastName
            }) as any;

            if (config.debug) { console.log('created new user', createdUser); }

            incrementProgress();

            userState = {
              ...userState,
              contentID: createdUser.uid || '',
              username: createdUser.username || '',
              email: createdUser.email || ''
            }

            dispatch({
              type: actions.UPDATE_USER,
              payload: userState
            })
          }

        }

        incrementProgress();
        
        // user is authorized to enter the application
        setReady(true);

      } catch (err) {
        if (config.debug) { console.log('/getauth ERROR', err); }

        // initiate the SAML user flow
        window.location.replace(config.revanceSSOEndpoint.URL + '/_saml');
      }

      // END SSO
    }
    init();

  }, []);

  return (
    <Route render={(props) =>
      !!ready || !config.features.userAuthentication ? (
        <Component {...props} />
      ) : (
          <StyledPageContainer bgImage={backgroundImg}>
            <div className='loading-container'>
              {/* <div className='loading-logo'><img className='loading-img' src={LogoImg} alt='Revance U logo' /><span> | GALLERY</span></div> */}
              <img className='loading-img' src={LogoImg} alt='Revance U logo' />
              <p className='loading-text'>Loading...</p>
              <div className='progress-bar'>
                <div className='progress' style={{ width: Math.round(progress / progressSteps * 100).toString() + '%' }} />
              </div>
              <p className='progress-text'>{Math.round(progress / progressSteps * 100).toString() + '%'}</p>
            </div>
          </StyledPageContainer>
        )
    }
    />
  );
}

export default PrivateRoute;



// WEBPACK FOOTER //
// ./src/components/PrivateRoute.tsx